import React, { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, SearchIcon } from 'lucide-react';
import { RadialBarChart, RadialBar, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
import { useSupabaseQuery } from '../hooks/useSupabaseQuery';
import { ChartSkeleton, TableSkeleton } from '../components/LoadingSkeleton';
import { DataTable } from '../components/DataTable';
const COLORS_REDEPLOY = ['#22d3ee', '#0ea5e9', '#0284c7'];
const COLORS_MATERIAL = ['#fbbf24', '#f59e0b', '#d97706'];
const COLORS_NMS = ['#34d399', '#10b981', '#059669'];

/* =========================
   🔥 CUSTOM TOOLTIP FIX
========================= */
const CustomTooltip = ({
  active,
  payload
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return <div style={{
      backgroundColor: '#020617',
      border: '1px solid #334155',
      borderRadius: '10px',
      padding: '10px',
      color: '#e2e8f0'
    }} data-id="element-992" data-mp="1vdh6z2pvqbnp">
        <div style={{
        fontSize: '12px',
        marginBottom: '4px',
        color: '#94a3b8'
      }} data-id="element-993" data-mp="2a94u7u6fbf9j">
          {data.name}
        </div>
        <div style={{
        fontWeight: '600',
        color: '#ffffff'
      }} data-id="element-994" data-mp="2f9iuur5p3mxj">
          {data.value} Sites
        </div>
      </div>;
  }
  return null;
};
export function StatusMonitoring() {
  const [page, setPage] = useState(0);
  const LIMIT = 10;

  // 🔍 SEARCH (DEBOUNCE)
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const delay = setTimeout(() => {
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchInput]);

  // DATA (CHART)
  const {
    data: allData,
    loading: allLoading,
    error
  } = useSupabaseQuery({
    limit: 5000
  });

  // DATA (TABLE)
  const {
    data: tableData,
    count,
    loading: tableLoading
  } = useSupabaseQuery({
    limit: LIMIT,
    page,
    searchQuery: search
  });

  // PROCESS DATA
  const {
    chartData,
    alerts
  } = useMemo(() => {
    if (!allData.length) return {
      chartData: null,
      alerts: []
    };
    const redeployMap = new Map();
    const materialMap = new Map();
    const nmsMap = new Map();
    const problematicSites = [];
    allData.forEach(item => {
      if (item.Status_Redeploy) redeployMap.set(item.Status_Redeploy, (redeployMap.get(item.Status_Redeploy) || 0) + 1);
      if (item.StatusMaterial) materialMap.set(item.StatusMaterial, (materialMap.get(item.StatusMaterial) || 0) + 1);
      if (item.Status_koneksi_NMS) nmsMap.set(item.Status_koneksi_NMS, (nmsMap.get(item.Status_koneksi_NMS) || 0) + 1);
      if (item.Status_koneksi_NMS !== 'Active' || item.StatusMaterial?.toLowerCase().includes('pending') || item.Status_Redeploy?.toLowerCase().includes('issue')) {
        problematicSites.push(item);
      }
    });
    const format = map => Array.from(map, ([name, value]) => ({
      name,
      value
    }));
    return {
      chartData: {
        redeploy: format(redeployMap),
        material: format(materialMap),
        nms: format(nmsMap)
      },
      alerts: problematicSites.slice(0, 5)
    };
  }, [allData]);
  if (error) return <div className="text-red-400 p-6" data-id="element-995" data-mp="2rem391uipcm">{error}</div>;

  // =========================
  // CHART COMPONENT
  // =========================

  const renderRadialChart = (data, title, colors) => <div className="glass-panel p-6 rounded-xl h-80" data-id="element-996" data-mp="3p6pxml02gabh">
      <h3 className="text-sm text-slate-300 mb-4 uppercase" data-id="element-997" data-mp="3p3v9aidxm5cs">{title}</h3>

      <ResponsiveContainer width="100%" height="100%" data-id="element-998" data-mp="3u3gaz0khkrt7">
        <RadialBarChart innerRadius="30%" outerRadius="90%" data={data} data-id="element-999" data-mp="lphdtvls30e4">
          <RadialBar dataKey="value" cornerRadius={10} data-id="element-1000" data-mp="lnefmoah20yb">
            {data.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} data-id="element-1001" data-mp="qi0reskgtvix" data-mpi={i} />)}
          </RadialBar>

          {/* 🔥 FIXED TOOLTIP */}
          <Tooltip content={<CustomTooltip data-id="element-1003" data-mp="2tww2kup30lpx" />} data-id="element-1002" data-mp="2txe8kj1jqeb1" />

          <Legend wrapperStyle={{
          color: '#e2e8f0',
          fontSize: '12px'
        }} data-id="element-1004" data-mp="2twmc7604kcjp" />
        </RadialBarChart>
      </ResponsiveContainer>
    </div>;
  const renderBarChart = (data, title, color) => <div className="glass-panel p-6 rounded-xl h-80" data-id="element-1005" data-mp="3ho1kdfel1o2v">
      <h3 className="text-sm text-slate-300 mb-4 uppercase" data-id="element-1006" data-mp="3hpkwqux4bezd">{title}</h3>

      <ResponsiveContainer width="100%" height="100%" data-id="element-1007" data-mp="3cpqaj4oafdkn">
        <BarChart data={data} layout="vertical" data-id="element-1008" data-mp="37xf9tzlq3nr1">
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" data-id="element-1009" data-mp="37w459xkgebyv" />

          <XAxis type="number" stroke="#cbd5f5" data-id="element-1010" data-mp="32zdo16wwxabv" />
          <YAxis dataKey="name" type="category" stroke="#cbd5f5" data-id="element-1011" data-mp="32wj3zght9riz" />

          <Tooltip formatter={(value, name, props) => [`${value} Sites`, props.payload.name // ✅ tampil Pohon / Clear
        ]} labelFormatter={() => ''} contentStyle={{
          backgroundColor: '#020617',
          border: '1px solid #334155',
          borderRadius: '10px',
          color: '#e2e8f0'
        }} data-id="element-1012" data-mp="49dibquwg9y0" />

          {/* ❌ HAPUS LEGEND */}
          {/* <Legend /> */}

          <Bar dataKey="value" fill={color} radius={[0, 8, 8, 0]} data-id="element-1013" data-mp="lrc10oh8kkrp" />
        </BarChart>
      </ResponsiveContainer>
    </div>;
  return <div className="space-y-6" data-id="element-1014" data-mp="qp58dku9zktk">
      <h2 className="text-xl font-bold text-slate-100" data-id="element-1015" data-mp="uqqpxjhr8na3">Status Monitoring</h2>

      {/* ALERT */}
      {!allLoading && alerts.length > 0 && <div className="bg-amber-950/30 border border-amber-900/50 rounded-xl p-6" data-id="element-1016" data-mp="zj0eow74vhas">
          <div className="flex items-center gap-2 text-amber-500 mb-4" data-id="element-1017" data-mp="14fqs1brjzysi">
            <AlertTriangleIcon size={20} data-id="element-1018" data-mp="19g4xcm1rsumu" />
            <h3 className="text-sm uppercase" data-id="element-1019" data-mp="19hg3utmloghn">Problematic Sites</h3>
          </div>

          <div className="grid md:grid-cols-3 gap-4" data-id="element-1020" data-mp="1eg12iom7es3z">
            {alerts.map((site, i) => <div key={i} className="bg-slate-900 p-4 rounded-lg" data-id="element-1021" data-mp="orvo5ufsxhrs" data-mpi={i}>
                <div data-id="element-1022" data-mp="jrzmi1uku2fe">{site.Site_ID_NE}</div>
                <div className="text-xs text-slate-400" data-id="element-1023" data-mp="jtaopx8dj24e">
                  {site.Site_Name_NE}
                </div>
              </div>)}
          </div>
        </div>}

      {/* CHART */}
      <div className="grid md:grid-cols-3 gap-6" data-id="element-1024" data-mp="tocg6zq4gga5">
        {allLoading || !chartData ? <>
            <ChartSkeleton data-id="element-1025" data-mp="17fh6vcbihy8c" />
            <ChartSkeleton data-id="element-1026" data-mp="12i8w3wsp0wh4" />
            <ChartSkeleton data-id="element-1027" data-mp="12j0gebgub7s3" />
          </> : <>
            {renderRadialChart(chartData.redeploy, 'Redeploy', COLORS_REDEPLOY)}
            {renderBarChart(chartData.material, 'Material', '#fbbf24')}
            {renderBarChart(chartData.nms, 'NMS', '#34d399')}
          </>}
      </div>

      {/* SEARCH */}
      <div className="flex justify-between items-center" data-id="element-1028" data-mp="3j74lvd47wxfc">
        <h3 className="text-sm text-slate-300" data-id="element-1029" data-mp="3e8jnlj8j0ntj">Full Data</h3>

        <div className="relative" data-id="element-1030" data-mp="3ea4hd6n0o2qc">
          <SearchIcon size={16} className="absolute left-3 top-2.5 text-slate-500" data-id="element-1031" data-mp="1tph8gepiuy11" />
          <input type="text" placeholder="Search site..." value={searchInput} onChange={e => {
          setSearchInput(e.target.value);
          setPage(0);
        }} className="pl-9 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-slate-200 focus:ring-1 focus:ring-cyan-500 outline-none" data-id="element-1032" data-mp="1yniqqxf4itbg" />
        </div>
      </div>

      {/* TABLE */}
      {tableLoading ? <TableSkeleton data-id="element-1033" data-mp="111lbd8w1j43q" /> : <DataTable data={tableData} columns={tableData.length > 0 ? Object.keys(tableData[0]).map(key => ({
      header: key.replaceAll('_', ' ').toUpperCase(),
      accessor: key
    })) : []} page={page} totalPages={Math.ceil(count / LIMIT)} onPageChange={setPage} data-id="element-1034" data-mp="112mnzhspfynk" />}
    </div>;
}